import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader } from "@/components/common/loader";
import { getAlertSummary, markAlertsViewed } from "@/features/alerts/api";
import type { AlertCategory, AlertGroup } from "@/features/alerts/types";
import { formatShortDate } from "@/lib/format";

const CATEGORY_LABELS: Record<AlertCategory, string> = {
  portal: "Portal",
  dashboard: "E-Signatures",
  invoices: "Invoices",
  emails: "Email",
  transcripts: "Transcripts",
};

/** Firm alert feed for the Control Center "Alerts" tab — portal activity,
 * invoice notices, and email bounces, grouped by client + category with a
 * server-computed "new since last view" badge. Renders bare (no Card) for
 * embedding in the Control Center tab body. */
export function AlertsFeed() {
  const [groups, setGroups] = useState<AlertGroup[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    getAlertSummary()
      .then((summary) => {
        if (!active) return;
        setGroups(summary.groups);
        // Advance each shown category's marker so these stop counting as "new"
        // next visit. Fire-and-forget; a failure just leaves the marker be.
        const categories = new Set(summary.groups.map((g) => g.category));
        for (const category of categories) void markAlertsViewed(category);
      })
      .catch(() => active && toast.error("Couldn't load alerts."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader />
      </div>
    );
  }

  if (groups.length === 0) {
    return <p className="text-muted-foreground py-8 text-center text-sm">No alerts.</p>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="[&>th]:text-muted-foreground [&>th]:text-xs [&>th]:font-semibold [&>th]:tracking-wide [&>th]:uppercase">
          <TableHead>Client</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Latest</TableHead>
          <TableHead className="text-right">Alerts</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {groups.map((g) => (
          <TableRow key={`${g.client ?? "firm"}:${g.category}`} className="hover:bg-accent">
            <TableCell className="font-medium">
              {g.client ? (
                <Link
                  to={`/client/${g.client}/overview`}
                  className="hover:text-primary hover:underline"
                >
                  {g.clientName ?? "Client"}
                </Link>
              ) : (
                <span className="text-muted-foreground">Firm-wide</span>
              )}
              {g.newCount > 0 && (
                <Badge variant="destructive" className="ml-2 px-1.5 text-[10px]">
                  {g.newCount} new
                </Badge>
              )}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {CATEGORY_LABELS[g.category]}
            </TableCell>
            <TableCell className="text-muted-foreground text-sm">
              {formatShortDate(g.latestAt)}
            </TableCell>
            <TableCell className="text-right font-semibold">{g.totalCount}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
